import moment from 'moment';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';

import {
  ControlContainer,
  NgForm,
  NgModel
} from '@angular/forms';

import { DATE_CONFIGURATIONS }  from '../../constants/date.configurations.constant';
import { TextControlComponent } from '../text-control/text-control.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector       : 'wor-time-control',
  template: require('./time-control.component.html'),
  viewProviders  : [
    {
      provide    : ControlContainer,
      useExisting: NgForm
    }
  ]
})
export class TimeControlComponent extends TextControlComponent implements AfterViewInit, OnDestroy {
  public _model        : string;
  public _picker       : any;
  public _timeSettings = DATE_CONFIGURATIONS.time;

  private _typed : string = null;

  @Input() isReadonly = false;
  @Input() viewFormat = DATE_CONFIGURATIONS.time.formats.view;

  @Input() set model ( _model : string ) {
    // A keystroke handed back by a two-way binding is still being typed: it is read on blur, not here.
    if ( _model && _model === this._typed ) {
      return;
    }

    this._model = moment(_model, this._timeSettings.formats.model).isValid()
      ? this.parseModel(_model)
      : _model;
  }

  get model () : string {
    return this.formatModel(this._model);
  }

  @ViewChild('input')  ngModel      : NgModel;
  @ViewChild('picker') pickerButton : ElementRef<HTMLButtonElement>;

  constructor (
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  // Reads typed time as the AngularJS field does: 12-hour (930, 9:30pm, 12a) or 24-hour (1300, 23:59).
  private _toModel ( view : string ) : string {
    const match = this._timeSettings.validation.exec(view);

    if ( !match ) {
      return view;
    }

    const isTwelveHour = match[5] === undefined;
    const meridian     = ( match[4] || '' ).toLowerCase();

    let hours = +( isTwelveHour ? match[2] : match[5] );

    if ( isTwelveHour && hours !== 12 && meridian.startsWith('p') ) {
      hours += 12;
    }
    else if ( isTwelveHour && hours === 12 && meridian.startsWith('a') ) {
      hours = 0;
    }

    return moment({
      hour  : hours,
      minute: +( ( isTwelveHour ? match[3] : match[6] ) || 0 )
    })
    .format(this._timeSettings.formats.model);
  }

  ngAfterViewInit () : void {
    this._picker = $(this.pickerButton.nativeElement).datetimepicker({
      initialDate : new Date(),
      showMeridian: true,
      startView   : 'day'
    })
    .on('changeDate', ( event : any ) => this.handleTimeSelection(event));
  }

  ngOnDestroy () : void {
    if (this._picker) {
      this._picker.datetimepicker('remove');
    }
  }

  formatModel ( model = this._model ) : string {
    const view = ( model || '' ).trim();

    return view
      ? this._toModel(view)
      : '';
  }

  handleBlur ( event : FocusEvent ) : void {
    this._typed = null;

    if (this.ngModel.control.valid) {
      this.model = this.formatModel(this._model);

      this.modelChange.emit(this.model);
    }

    this.blur.emit(event);
  }

  handleTimeSelection ( event : any ) : void {
    const date = event.date;

    if ( moment(date).isValid ) {
      this.model = moment(date)
      .utc()
      .set({ second: 0 })
      .format(this._timeSettings.formats.model);

      this.modelChange.emit(this.model);

      // The picker's event does not mark this OnPush view, so the error would stay red.
      this.ngModel.control.setValue(this._model, { emitViewToModelChange: false });
      this.changeDetectorRef.markForCheck();
    }


    this._picker.datetimepicker('hide');
  }

  onModelChange () : void {
    this._typed = this._model;

    this.modelChange.emit(this._model);
  }

  parseModel ( model : string ) : string {
    return moment(model, this._timeSettings.formats.model)
      .format(this.viewFormat);
  }
}
